/**
 * Invoice Form Component (T089)
 * Reusable invoice form with customer search, line items, and tax calculation
 */

import { useState, useEffect, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Search, Plus, Trash2, User, AlertTriangle, Eye } from 'lucide-react';
import apiClient, { type ApiResponse } from '../../lib/api-client';
import { Modal } from '../ui';
import InvoicePreview from './InvoicePreview';

// Types
interface Customer {
  id: string;
  name: string;
  email: string;
  phone?: string;
  address?: string;
  taxExempt: boolean;
}

interface CustomersResponse {
  customers: Customer[];
  pagination: { total: number; page: number; limit: number; totalPages: number };
}

export interface LineItem {
  description: string;
  quantity: number;
  unitPrice: number;
}

export interface InvoiceFormData {
  customerId: string;
  appointmentId?: string;
  dueDate: string;
  lineItems: LineItem[];
  notes?: string;
}

interface InvoiceFormProps {
  initialData?: InvoiceFormData & {
    customerName?: string;
    customerEmail?: string;
    customerPhone?: string;
    taxExempt?: boolean;
  };
  onSubmit: (data: InvoiceFormData) => void;
  onSaveDraft?: (data: InvoiceFormData) => void;
  isSubmitting?: boolean;
  submitLabel?: string;
}

const DEFAULT_TAX_RATE = 8.875; // Default tax rate percentage

const emptyLineItem = (): LineItem => ({
  description: '',
  quantity: 1,
  unitPrice: 0,
});

const formatCurrency = (amount: number) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);

export default function InvoiceForm({
  initialData,
  onSubmit,
  onSaveDraft,
  isSubmitting = false,
  submitLabel = 'Create Invoice',
}: InvoiceFormProps) {
  // Customer state
  const [customerSearch, setCustomerSearch] = useState('');
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
  const [showCustomerDropdown, setShowCustomerDropdown] = useState(false);

  // Form state
  const [dueDate, setDueDate] = useState(
    initialData?.dueDate ||
      new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]
  );
  const [lineItems, setLineItems] = useState<LineItem[]>(
    initialData?.lineItems?.length ? initialData.lineItems : [emptyLineItem()]
  );
  const [notes, setNotes] = useState(initialData?.notes || '');
  const [showPreview, setShowPreview] = useState(false);

  // Pre-fill customer from initial data
  useEffect(() => {
    if (initialData?.customerId && initialData.customerName) {
      setSelectedCustomer({
        id: initialData.customerId,
        name: initialData.customerName,
        email: initialData.customerEmail || '',
        phone: initialData.customerPhone,
        taxExempt: initialData.taxExempt || false,
      });
    }
  }, [
    initialData?.customerId,
    initialData?.customerName,
    initialData?.customerEmail,
    initialData?.customerPhone,
    initialData?.taxExempt,
  ]);

  // Customer search query
  const { data: customersData } = useQuery({
    queryKey: ['customers-search', customerSearch],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (customerSearch) {
        params.append('search', customerSearch);
      }
      params.append('limit', '10');
      const response = await apiClient.get<ApiResponse<CustomersResponse>>(
        `/customers?${params.toString()}`
      );
      return response.data.data;
    },
    enabled: customerSearch.length >= 1 && showCustomerDropdown,
  });

  const customers = customersData?.customers || [];

  // Calculations
  const subtotal = lineItems.reduce((sum, item) => sum + item.quantity * item.unitPrice, 0);
  const isTaxExempt = selectedCustomer?.taxExempt || false;
  const taxRate = isTaxExempt ? 0 : DEFAULT_TAX_RATE;
  const tax = subtotal * (taxRate / 100);
  const total = subtotal + tax;

  // Line item handlers
  const addLineItem = () => setLineItems([...lineItems, emptyLineItem()]);

  const removeLineItem = (index: number) => {
    if (lineItems.length <= 1) {
      return;
    }
    setLineItems(lineItems.filter((_, i) => i !== index));
  };

  const updateLineItem = (index: number, field: keyof LineItem, value: string | number) => {
    const updated = [...lineItems];
    if (field === 'description') {
      updated[index] = { ...updated[index], description: value as string };
    } else {
      updated[index] = { ...updated[index], [field]: Number(value) || 0 };
    }
    setLineItems(updated);
  };

  const buildFormData = useCallback(
    (): InvoiceFormData => ({
      customerId: selectedCustomer?.id || '',
      dueDate,
      lineItems: lineItems.filter((item) => item.description.trim()),
      notes: notes || undefined,
    }),
    [selectedCustomer, dueDate, lineItems, notes]
  );

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(buildFormData());
  };

  const handleSaveDraft = () => {
    onSaveDraft?.(buildFormData());
  };

  const previewData = {
    customerName: selectedCustomer?.name || 'No customer selected',
    customerEmail: selectedCustomer?.email || '',
    customerPhone: selectedCustomer?.phone,
    dueDate,
    lineItems,
    subtotal,
    taxRate,
    tax,
    total,
    notes,
    taxExempt: isTaxExempt,
  };

  return (
    <>
      <form onSubmit={handleSubmit} className="space-y-8">
        {/* Customer Selection */}
        <div className="claude-card p-6 space-y-4">
          <h3 className="text-lg font-semibold">Customer</h3>

          {selectedCustomer ? (
            <div className="flex items-start justify-between p-4 rounded-lg bg-primary/5 border border-primary/20">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                  <User className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <p className="font-semibold">{selectedCustomer.name}</p>
                  <p className="text-sm text-muted-foreground">{selectedCustomer.email}</p>
                  {selectedCustomer.phone && (
                    <p className="text-sm text-muted-foreground">{selectedCustomer.phone}</p>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-2">
                {isTaxExempt && (
                  <span className="inline-flex items-center gap-1 text-xs font-medium px-2 py-1 rounded bg-warning/10 text-warning">
                    <AlertTriangle className="w-3 h-3" />
                    Tax Exempt
                  </span>
                )}
                <button
                  type="button"
                  onClick={() => {
                    setSelectedCustomer(null);
                    setCustomerSearch('');
                  }}
                  className="claude-button claude-button-ghost text-sm"
                >
                  Change
                </button>
              </div>
            </div>
          ) : (
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground w-4 h-4" />
              <input
                type="text"
                placeholder="Search customers by name or email..."
                value={customerSearch}
                onChange={(e) => {
                  setCustomerSearch(e.target.value);
                  setShowCustomerDropdown(true);
                }}
                onFocus={() => setShowCustomerDropdown(true)}
                className="claude-input pl-10 w-full"
              />
              {showCustomerDropdown && customers.length > 0 && (
                <div className="absolute z-20 mt-1 w-full claude-card border border-border shadow-lg max-h-60 overflow-y-auto">
                  {customers.map((customer) => (
                    <button
                      key={customer.id}
                      type="button"
                      onClick={() => {
                        setSelectedCustomer(customer);
                        setShowCustomerDropdown(false);
                        setCustomerSearch('');
                      }}
                      className="w-full text-left px-4 py-3 hover:bg-muted/50 transition-colors flex items-center gap-3"
                    >
                      <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
                        <User className="w-4 h-4 text-primary" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="font-medium truncate">{customer.name}</p>
                        <p className="text-xs text-muted-foreground truncate">{customer.email}</p>
                      </div>
                      {customer.taxExempt && (
                        <span className="text-xs text-warning font-medium">Tax Exempt</span>
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Line Items */}
        <div className="claude-card p-6 space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold">Line Items</h3>
            <button
              type="button"
              onClick={addLineItem}
              className="claude-button claude-button-secondary text-sm"
            >
              <Plus className="w-4 h-4" />
              Add Item
            </button>
          </div>

          <div className="space-y-3">
            {/* Header */}
            <div className="grid grid-cols-[1fr_80px_120px_100px_40px] gap-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider px-1">
              <span>Description</span>
              <span className="text-right">Qty</span>
              <span className="text-right">Unit Price</span>
              <span className="text-right">Amount</span>
              <span></span>
            </div>

            {lineItems.map((item, index) => (
              <div
                key={index}
                className="grid grid-cols-[1fr_80px_120px_100px_40px] gap-3 items-center"
              >
                <input
                  type="text"
                  placeholder="Service description"
                  value={item.description}
                  onChange={(e) => updateLineItem(index, 'description', e.target.value)}
                  className="claude-input"
                />
                <input
                  type="number"
                  min="1"
                  value={item.quantity}
                  onChange={(e) => updateLineItem(index, 'quantity', e.target.value)}
                  className="claude-input text-right"
                />
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={item.unitPrice || ''}
                  onChange={(e) => updateLineItem(index, 'unitPrice', e.target.value)}
                  className="claude-input text-right"
                  placeholder="0.00"
                />
                <span className="text-right font-medium text-sm">
                  {formatCurrency(item.quantity * item.unitPrice)}
                </span>
                <button
                  type="button"
                  onClick={() => removeLineItem(index)}
                  disabled={lineItems.length <= 1}
                  className="claude-button claude-button-ghost p-2 text-destructive disabled:opacity-30"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>

          {/* Totals */}
          <div className="border-t border-border pt-4 flex justify-end">
            <div className="w-64 space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Subtotal</span>
                <span className="font-medium">{formatCurrency(subtotal)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">
                  Tax {isTaxExempt ? '(Exempt)' : `(${taxRate}%)`}
                </span>
                <span className="font-medium">{formatCurrency(tax)}</span>
              </div>
              <div className="flex justify-between text-lg font-bold border-t border-border pt-2">
                <span>Total</span>
                <span>{formatCurrency(total)}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Due Date & Notes */}
        <div className="claude-card p-6 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <label className="text-sm font-medium">Due Date</label>
              <input
                type="date"
                value={dueDate}
                onChange={(e) => setDueDate(e.target.value)}
                className="claude-input w-full"
              />
            </div>
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium">Notes</label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Additional notes for the customer..."
              rows={3}
              className="claude-input w-full resize-none"
            />
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center justify-between">
          <button
            type="button"
            onClick={() => setShowPreview(true)}
            className="claude-button claude-button-secondary"
          >
            <Eye className="w-4 h-4" />
            Preview
          </button>
          <div className="flex items-center gap-3">
            {onSaveDraft && (
              <button
                type="button"
                onClick={handleSaveDraft}
                disabled={isSubmitting || !selectedCustomer}
                className="claude-button claude-button-secondary"
              >
                Save as Draft
              </button>
            )}
            <button
              type="submit"
              disabled={
                isSubmitting || !selectedCustomer || lineItems.every((i) => !i.description.trim())
              }
              className="claude-button claude-button-primary"
            >
              {isSubmitting ? (
                <>
                  <div className="claude-spinner w-4 h-4" />
                  Saving...
                </>
              ) : (
                submitLabel
              )}
            </button>
          </div>
        </div>
      </form>

      {/* Preview Modal */}
      <Modal
        isOpen={showPreview}
        onClose={() => setShowPreview(false)}
        title="Invoice Preview"
        size="xl"
      >
        <InvoicePreview invoice={previewData} />
      </Modal>
    </>
  );
}
